// ABOUTME: Hook for copying text to clipboard with fallback for older browsers.
// ABOUTME: Provides unified clipboard functionality with optional notifications.

import { useCallback } from "react"
import { useNotificationContext } from "@/app/providers/useNotificationContext"
import { useTranslation } from "react-i18next"

interface CopyToClipboardOptions {
  /** Show success notification after copying */
  showSuccessNotification?: boolean
  /** Show error notification on failure */
  showErrorNotification?: boolean
  /** Custom success message (uses t("common.copied") by default) */
  successMessage?: string
  /** Custom error message (uses t("common.copyError") by default) */
  errorMessage?: string
}

interface UseCopyToClipboardReturn {
  /** Copy text to clipboard */
  copyText: (text: string, options?: CopyToClipboardOptions) => Promise<boolean>
  /** Copy link to clipboard (alias for copyText with link-specific messages) */
  copyLink: (link: string) => Promise<boolean>
}

/**
 * Fallback copy method for older browsers or non-secure contexts
 */
const fallbackCopyText = (text: string): boolean => {
  const textArea = document.createElement("textarea")
  textArea.value = text
  textArea.style.position = "fixed"
  textArea.style.left = "-999999px"
  textArea.style.top = "-999999px"
  document.body.appendChild(textArea)
  textArea.focus()
  textArea.select()

  try {
    document.execCommand("copy")
    return true
  } catch {
    return false
  } finally {
    document.body.removeChild(textArea)
  }
}

export const useCopyToClipboard = (): UseCopyToClipboardReturn => {
  const { handleNotification } = useNotificationContext()
  const { t } = useTranslation()

  const copyText = useCallback(
    async (text: string, options: CopyToClipboardOptions = {}): Promise<boolean> => {
      const { showSuccessNotification = true, showErrorNotification = true, successMessage, errorMessage } = options

      try {
        if (window.isSecureContext) {
          await navigator.clipboard.writeText(text)
        } else {
          const success = fallbackCopyText(text)
          if (!success) {
            throw new Error("Fallback copy failed")
          }
        }

        if (showSuccessNotification) {
          handleNotification("success", successMessage ?? t("common.copied"), true, 3000)
        }
        return true
      } catch {
        if (showErrorNotification) {
          handleNotification("error", errorMessage ?? t("common.copyError"), true, 3000)
        }
        return false
      }
    },
    [handleNotification, t],
  )

  const copyLink = useCallback(
    async (link: string): Promise<boolean> => {
      return copyText(link, {
        successMessage: t("common.linkCopied"),
        errorMessage: t("common.copyLinkError"),
      })
    },
    [copyText, t],
  )

  return { copyText, copyLink }
}
